// UndoBaselineStore — the client "B' baseline canvas" mechanism for
// conflict-aware Undo (docs/_wip/undo-conflict-aware-2026-07-12.md 「クライア
// ント側の復元コスト」/「設計確定 f4」, protocol-events.md §1.1).
//
// This is a client-side MIRROR of the server's session buffer: a "baseline"
// canvas (= what the last locally-baked snapshot generation looks like) plus
// the ordered list of strokes that have landed since. It exists so that
// `stroke.cancelled` (a bbox-local rewind) never requires re-fetching the
// snapshot PNG or replaying the WHOLE canvas — only the strokes intersecting
// the cancelled stroke's bbox need to be re-applied, and only within that
// bbox (doc: 「bbox の外は 1 ピクセルも変わらない → 全画面 replay は原理的に
// 不要」).
//
// Responsibilities (architecture.md §19.10 A/C/E friendly — this module is a
// plain class with no DOM dependency, reusable by the worker or the inline
// main-thread engine):
//
//   1. Ingest every `stroke.start` / `stroke.segment` / `stroke.end`
//      broadcast (self AND remote — the store needs full stroke metadata +
//      geometry for every sid, not just what's rendered) plus
//      `stroke.cancelled` tombstones and `snapshot.published` watermarks.
//      Also ingest the two other raster-mutating broadcasts (2026-07-23
//      「第二弾設計確定」imagepaste / per-layer clear × Undo 穴埋め —
//      without this, `whiteboard.imagepaste` / `whiteboard.clear` never land
//      in the baseline or a bbox-local rewind, so a cancelled stroke UNDER a
//      later paste/clear repairs to the WRONG pixels):
//        - `whiteboard.imagepaste` (`onImagepaste` for the metadata,
//          synchronously at broadcast time; `onImagepasteBitmap` once the
//          SAME fetch+decode the display path performs resolves — see that
//          method's doc for why the split matters).
//        - `whiteboard.clear` (`onClear`, per-layer OR full — `layerId
//          undefined` mirrors the broadcast's own "clear every layer"
//          convention).
//      Strokes / imagepastes / clears share ONE global server-assigned `seq`
//      ordering space, so bake (`bakeStep`) and rewind (`localRepair`) merge
//      all three kinds by `seq` (stroke's ordering key is its `endSeq` — see
//      the "Replay-order note" below) to reproduce the exact order the
//      server's own PNG-generating worker applies them in.
//   2. On a batch of `stroke.cancelled`s (f4 coalescing — "同 tick に溜まった
//      cancel を union bbox で 1 パス処理"): `drainRewind()` unions the
//      queued bboxes PER LAYER and does one local-repair pass per layer,
//      returning ready-to-blit RGBA patches. The caller (`CanvasDrawingEngine`
//      / drawing worker) applies each patch via `EngineCore.applyLayerRegion`.
//   3. On `snapshot.published { snapshotSeq }`: lazily / time-sliced bakes
//      eligible strokes (`endSeq <= snapshotSeq && !cancelled`) into the
//      baseline via `bakeStep()`, trimming them from the pending-stroke list
//      as it goes (f4: 「watermark を進めて stroke リストを trim」). The
//      caller drives `bakeStep()` repeatedly (e.g. one call per idle tick)
//      until it returns `false` — never a single synchronous mega-bake (f4:
//      「同期一括 bake しない」, avoids a multi-hundred-stroke replay stalling
//      a frame every ~5 minutes).
//
// Rewind basis (f4, ABSOLUTE): a cancel's local repair is computed against
// `watermark` — the store's OWN actually-baked cut point — never against the
// `snapshotSeq` a `snapshot.published` broadcast carries. Bake is lazy /
// time-sliced, so those two can differ for a while; using the broadcast value
// would double-apply or omit strokes mid-bake.
//
// Replay-order note: strokes are baked / replayed in **ascending `endSeq`
// order** (matching the bake-eligibility rule and the server's own
// commit-at-end + pair-filter semantics, f4 "クライアントベースラインとサーバ
// PNG が世代ごとに同じものを表す"). This is the same "stroke-atomic replay in
// commit order" ground truth the equivalence harness
// (`replayEquivalence.property.test.ts`) and the server's snapshot worker use
// — NOT the true real-time interleaving of concurrently-drawn strokes'
// individual segments. That gap (fine-grained segment interleaving across
// two truly-concurrent overlapping brush strokes) is a pre-existing,
// accepted property of the system (2026-07-22 "実装フェーズ検証結果" §2 —
// live client vs server-PNG divergence for bridging semi-transparent
// strokes), not something this store introduces or is responsible for
// resolving.
import { EngineCore } from '../engineCore.js';
import { regionMoveFootprints } from '../regionMove.js';
import { isFlatPen } from '../strokeRenderer.js';
import { imagepasteFootprintBbox, rectsIntersect, strokeFootprintBbox, unionRects, } from './strokeGeometry.js';
/**
 * Per-`bakeStep()` cap on strokes processed. Keeps each call boundedly cheap
 * so the caller can time-slice across idle ticks (f4 "同期一括 bake しない").
 * Not a hard perf budget in ms (deterministic count is simpler to test) —
 * tune if a real workload shows a single step is still too heavy.
 */
const DEFAULT_BAKE_STEP_LIMIT = 32;
/** 描きかけの線を作り直しの再生順の最後に回すための seq (どの実 seq よりも後)。 */
const IN_FLIGHT_SEQ = Number.MAX_SAFE_INTEGER;
/**
 * (Adversarial-review fix C1) Safety valve for a `whiteboard.imagepaste`
 * fetch+decode that never settles (neither `onImagepasteBitmap(seq, bitmap)`
 * nor `onImagepasteBitmap(seq, null)` is ever called — e.g. a hung network
 * request; `CanvasDrawingEngine.fetchAndApplyImagepaste` has no fetch-level
 * timeout of its own, see the fix's accompanying report). Without this, a
 * single stuck fetch would stall `bakeStep`'s watermark FOREVER (see its
 * doc). 60s is deliberately generous relative to a normal image fetch+decode
 * (sub-second on any reasonable connection for a ≤5 MiB payload) while still
 * bounding the worst case to a fixed, short window — configurable via the
 * constructor's `pendingImagepasteTimeoutMs` option for tests.
 */
const DEFAULT_PENDING_IMAGEPASTE_TIMEOUT_MS = 60_000;
/**
 * (Adversarial-review fix C2) Total-bytes cap across every currently-tracked
 * (not yet baked/trimmed) `ClippedImagepasteBitmap.rgba`. Bounds worst-case
 * memory from several large, slow-to-bake pastes accumulating at once (e.g. a
 * busy multi-person room with a long `horizon`, per-connection rate limiting
 * means this scales with headcount not one bad actor). 128 MiB is a coarse
 * default (comfortably several canvas-sized pastes even at ~4000², the
 * upper end of `architecture.md`'s canvas size range) — configurable via
 * `imagepasteBitmapBytesCap` for callers with tighter budgets.
 */
const DEFAULT_IMAGEPASTE_BITMAP_BYTES_CAP = 128 * 1024 * 1024;
export class UndoBaselineStore {
    width;
    height;
    bakeStepLimit;
    now;
    pendingImagepasteTimeoutMs;
    imagepasteBitmapBytesCap;
    onImagepasteBitmapEvicted;
    /** Running total of every currently-tracked `ClippedImagepasteBitmap.rgba.byteLength` (C2 cap accounting). */
    pendingImagepasteBytes = 0;
    /** Count of C2 cap evictions so far — diagnostics / test introspection (`getImagepasteEvictionCount`). */
    imagepasteEvictionCount = 0;
    /**
     * The baseline canvas — mirrors the layer state after the most recently
     * baked stroke. No backend attached (headless raster only); layers are
     * indexed identically to the live engine.
     */
    baseline;
    /**
     * Reused scratch surface for local-repair replay (1 layer — every replay
     * pass targets a single `layerId`, remapped to layer 0 here regardless of
     * its true id). Persistent (not reallocated per rewind) so repeated
     * cancels don't pay a fresh `width*height*4` allocation each time.
     */
    scratch;
    strokes = new Map();
    /**
     * `whiteboard.imagepaste` / `whiteboard.clear` records, both keyed by their
     * server-stamped `seq` (globally unique — one shared counter across every
     * broadcast kind, so no collision risk with a different event's `seq`).
     * 2026-07-23 imagepaste/per-layer-clear × Undo 穴埋め — see module header.
     */
    imagepastes = new Map();
    clears = new Map();
    /** `whiteboard.region` records, keyed by server-stamped `seq` (same shared seq space). */
    regions = new Map();
    /** Actually-baked watermark (§f4 — NOT the last `snapshot.published` value). */
    watermark = 0;
    /** Highest `snapshotSeq` seen via `onSnapshotPublished` (bake target). */
    targetSnapshotSeq = 0;
    /** Cancel bboxes queued since the last `drainRewind()`, keyed by layerId (f4 coalescing). */
    pendingCancelBboxByLayer = new Map();
    constructor(width, height, layerCount = 1, bakeStepLimit = DEFAULT_BAKE_STEP_LIMIT, options = {}) {
        this.width = width;
        this.height = height;
        this.bakeStepLimit = bakeStepLimit;
        this.now = options.now ?? Date.now;
        this.pendingImagepasteTimeoutMs =
            options.pendingImagepasteTimeoutMs ?? DEFAULT_PENDING_IMAGEPASTE_TIMEOUT_MS;
        this.imagepasteBitmapBytesCap =
            options.imagepasteBitmapBytesCap ?? DEFAULT_IMAGEPASTE_BITMAP_BYTES_CAP;
        this.onImagepasteBitmapEvicted = options.onImagepasteBitmapEvicted ?? (() => { });
        this.baseline = new EngineCore({ width, height, layerCount });
        this.scratch = new EngineCore({ width, height, layerCount: 1 });
    }
    /**
     * Seed the baseline directly from already-baked pixels (a `room.welcome` /
     * `room.resume-ok` snapshot PNG the client just decoded, or a
     * `whiteboard.restore` seed) and raise the watermark to that snapshot's
     * `seq`. Without this, a freshly-constructed store's baseline is blank
     * (transparent) — a rewind computed before any `seedBaseline` call would
     * incorrectly fall back to blank pixels for bbox regions that were already
     * painted-and-baked before this client even joined.
     *
     * `layers` mirrors `EngineCore.applySnapshot`'s shape; a mismatched-length
     * `rgba` for a given `id` is silently skipped (defensive, matches
     * `EngineCore.applySnapshot`). Only raises `watermark` /
     * `targetSnapshotSeq` (never lowers — a stale/duplicate seed call is a
     * safe no-op for the watermark side). Any already-tracked stroke covered
     * by the new watermark is dropped (defensive — normally none exist yet
     * when this is called, since it precedes any `onStrokeStart`/`onStrokeEnd`
     * for the joined session).
     */
    seedBaseline(layers, watermarkSeq) {
        for (const { id, rgba } of layers) {
            const layer = this.baseline.getLayer(id);
            if (layer === undefined || rgba.length !== layer.main.length)
                continue;
            layer.main.set(rgba);
        }
        if (watermarkSeq > this.watermark)
            this.watermark = watermarkSeq;
        if (watermarkSeq > this.targetSnapshotSeq)
            this.targetSnapshotSeq = watermarkSeq;
        for (const [sid, rec] of this.strokes) {
            if (rec.endSeq !== null && rec.endSeq <= this.watermark)
                this.strokes.delete(sid);
        }
        // Same defensive drop for imagepaste/clear records (normally none exist
        // yet either, for the same reason — see the stroke loop's comment above).
        for (const seq of [...this.imagepastes.keys()]) {
            if (seq <= this.watermark)
                this.trimImagepaste(seq);
        }
        for (const seq of this.clears.keys()) {
            if (seq <= this.watermark)
                this.clears.delete(seq);
        }
        for (const seq of [...this.regions.keys()]) {
            if (seq <= this.watermark)
                this.trimRegion(seq);
        }
    }
    // ─── ingestion ────────────────────────────────────────────────────────
    /** Record a `stroke.start`. Idempotent (duplicate sid = no-op, e.g. replayed resume delta). */
    onStrokeStart(meta) {
        if (this.strokes.has(meta.sid))
            return;
        this.strokes.set(meta.sid, { ...meta, points: [], endSeq: null, cancelled: false, bbox: null });
    }
    /** Record a `stroke.segment`'s points. No-op for an unknown sid (untracked / already trimmed). */
    onStrokeSegment(sid, points) {
        const rec = this.strokes.get(sid);
        if (rec === undefined)
            return;
        for (const pt of points)
            rec.points.push(pt);
    }
    /** Record a `stroke.end`. Computes the stroke's (immutable) bbox. Idempotent. */
    onStrokeEnd(sid, endSeq) {
        const rec = this.strokes.get(sid);
        if (rec === undefined || rec.endSeq !== null)
            return;
        rec.endSeq = endSeq;
        rec.bbox = strokeFootprintBbox(rec.points, rec.width, this.width, this.height);
    }
    /**
     * Record a `stroke.cancelled` tombstone (f1). Marks the stroke cancelled
     * (excluded from all future bakes/replays) and queues its bbox for the
     * next `drainRewind()` (coalesced with any other cancel queued in the same
     * batch). Unknown sid is an idempotent no-op — per protocol-events.md §1.1
     * ("未知 sid は冪等無視", e.g. a late-joiner who never saw the original
     * stroke because welcome deltas are pair-filtered server-side).
     *
     * Returns the stroke's bbox (or `null` for an unknown/zero-point stroke) —
     * purely informational for callers that want to log/measure; the actual
     * rewind is produced by `drainRewind()`.
     */
    onCancelled(sid) {
        const rec = this.strokes.get(sid);
        if (rec === undefined || rec.cancelled)
            return null;
        rec.cancelled = true;
        if (rec.bbox === null)
            return null; // nothing painted, nothing to rewind
        const list = this.pendingCancelBboxByLayer.get(rec.layerId);
        if (list === undefined)
            this.pendingCancelBboxByLayer.set(rec.layerId, [rec.bbox]);
        else
            list.push(rec.bbox);
        return rec.bbox;
    }
    /**
     * Record a `whiteboard.imagepaste` broadcast's METADATA (layerId / x / y /
     * width / height / bbox) — independent of whether the decoded bitmap is
     * ready yet. Call this synchronously at broadcast-receive time (mirrors
     * `onStrokeStart`), NOT deferred until the async fetch+decode the display
     * path (`CanvasDrawingEngine.fetchAndApplyImagepaste`) performs resolves.
     *
     * This ordering matters: broadcasts arrive over one ordered stream, so by
     * the time a LATER `snapshot.published` or `stroke.cancelled` (whose
     * bake/rewind window could reach this `seq`) lands, this metadata is
     * already recorded here — even if the pixels themselves are still in
     * flight. `onImagepasteBitmap` attaches the pixels once that same fetch
     * resolves (success OR explicit failure).
     *
     * Idempotent per `seq` (server-stamped, globally unique across every
     * broadcast kind) — a duplicate ingest (e.g. resume/welcome re-delivery)
     * is a no-op.
     */
    onImagepaste(meta) {
        if (this.imagepastes.has(meta.seq))
            return;
        const bbox = imagepasteFootprintBbox(meta, this.width, this.height);
        this.imagepastes.set(meta.seq, {
            ...meta,
            bbox,
            bitmap: null,
            status: 'pending',
            pendingSince: this.now(),
        });
    }
    /**
     * Attach the decoded bitmap for a previously-`onImagepaste`d paste (`seq`)
     * once `CanvasDrawingEngine`'s fetch+decode (`ImagepasteFetchFn` /
     * `ImagepasteCreateBitmapFn`, Phase 7 Task 7-8a) settles. Pass the decoded
     * RGBA on success, or `null` explicitly on failure — mirrors the display
     * path's own "fetch/decode failed → drop, paint nothing" outcome, so the
     * baseline never ends up with pixels the live canvas doesn't have.
     *
     * **Adversarial-review fix (C1, 2026-07-24)**: success now moves `status`
     * from `'pending'` to `'resolved'` (see `bakeStep`'s doc for why this
     * matters — `bakeStep` STALLS rather than skipping a still-`'pending'`
     * paste, so this call is what unblocks it). The RAW `bitmap` is clipped to
     * the canvas-intersecting region before being retained (C2, see
     * `ClippedImagepasteBitmap`'s doc) and counted against the C2 byte cap
     * (may trigger evicting an older resolved bitmap — `enforceImagepasteBitmapCap`).
     *
     * A `seq` that's unknown here (bake already trimmed it — a confirmed
     * failure, a timed-out/cap-evicted paste, or a genuinely-resolved one) is a
     * no-op. Also a no-op once `status === 'resolved'` (the fetch only ever
     * runs once per broadcast; the first successful attach wins and can never
     * be retroactively overwritten). Note the ASYMMETRY this leaves: a LATE
     * success arriving after this paste was already marked `'failed'` (by an
     * explicit `null` call, the pending-timeout safety valve, OR a C2 cap
     * eviction) but NOT YET trimmed (still blocked behind an even-earlier
     * unsettled paste) is accepted and resurrects it to `'resolved'` — since
     * nothing has been baked/skipped for it yet, incorporating the real pixels
     * once they're known is strictly more correct than leaving it failed.
     */
    onImagepasteBitmap(seq, bitmap) {
        const rec = this.imagepastes.get(seq);
        if (rec === undefined || rec.status === 'resolved')
            return;
        if (bitmap === null) {
            this.imagepastes.set(seq, { ...rec, status: 'failed', bitmap: null });
            return;
        }
        const clipped = this.clipImagepasteBitmap(rec.x, rec.y, bitmap);
        if (clipped !== null)
            this.trackImagepasteBitmapBytes(clipped.rgba.byteLength);
        this.imagepastes.set(seq, { ...rec, status: 'resolved', bitmap: clipped });
    }
    /**
     * (C2) Clip a raw decoded imagepaste bitmap to the canvas-intersecting
     * rectangle, returning `null` when the paste lands entirely off-canvas
     * (nothing to retain — matches `EngineCore.applyImagepaste`'s own
     * off-canvas no-op). Byte-exact with painting the unclipped buffer at
     * `(x, y)` — see `ClippedImagepasteBitmap`'s doc for the derivation.
     */
    clipImagepasteBitmap(x, y, bitmap) {
        const rawX = Math.floor(x);
        const rawY = Math.floor(y);
        const dstX0 = Math.max(0, rawX);
        const dstY0 = Math.max(0, rawY);
        const dstX1 = Math.min(this.width - 1, rawX + bitmap.width - 1);
        const dstY1 = Math.min(this.height - 1, rawY + bitmap.height - 1);
        if (dstX1 < dstX0 || dstY1 < dstY0)
            return null;
        const srcOffsetX = dstX0 - rawX;
        const srcOffsetY = dstY0 - rawY;
        const w = dstX1 - dstX0 + 1;
        const h = dstY1 - dstY0 + 1;
        const rgba = new Uint8ClampedArray(w * h * 4);
        const rowBytes = w * 4;
        for (let row = 0; row < h; row++) {
            const srcStart = ((srcOffsetY + row) * bitmap.width + srcOffsetX) * 4;
            rgba.set(bitmap.rgba.subarray(srcStart, srcStart + rowBytes), row * rowBytes);
        }
        return { rgba, width: w, height: h, x: dstX0, y: dstY0 };
    }
    /**
     * (C2) Account newly-retained bitmap bytes and enforce
     * `imagepasteBitmapBytesCap`, evicting the OLDEST (lowest-`seq`)
     * resolved-with-bitmap record(s) first if over budget. Evicting the
     * oldest minimises the accepted-divergence WINDOW: that record is closest
     * to its natural bake point anyway (bake proceeds in ascending seq order),
     * so forcing it to `'failed'` early costs the least. An evicted record is
     * marked `'failed'` (not deleted) — this reuses the C1 "confirmed
     * failure" contract (bake skips it, `bakeStep` never stalls on it) rather
     * than inventing a third divergence category.
     */
    trackImagepasteBitmapBytes(bytes) {
        this.pendingImagepasteBytes += bytes;
        this.enforceImagepasteBitmapCap();
    }
    /** 上限を超えている間、resolved な画像 (貼り付け・範囲の移動) を seq の古い順に 'failed' へ落とす。 */
    enforceImagepasteBitmapCap() {
        if (this.pendingImagepasteBytes <= this.imagepasteBitmapBytesCap)
            return;
        const candidates = [];
        for (const rec of this.imagepastes.values()) {
            if (rec.status !== 'resolved' || rec.bitmap === null)
                continue;
            const bytes = rec.bitmap.rgba.byteLength;
            candidates.push({
                seq: rec.seq,
                bytes,
                evict: () => this.imagepastes.set(rec.seq, { ...rec, status: 'failed', bitmap: null }),
            });
        }
        for (const rec of this.regions.values()) {
            if (rec.status !== 'resolved')
                continue;
            const bytes = rec.bitmaps.reduce((n, b) => n + (b === null ? 0 : b.rgba.byteLength), 0);
            if (bytes === 0)
                continue;
            candidates.push({
                seq: rec.meta.seq,
                bytes,
                evict: () => {
                    rec.status = 'failed';
                    rec.bitmaps = rec.meta.items.map(() => null);
                },
            });
        }
        candidates.sort((a, b) => a.seq - b.seq);
        for (const c of candidates) {
            if (this.pendingImagepasteBytes <= this.imagepasteBitmapBytesCap)
                break;
            this.pendingImagepasteBytes -= c.bytes;
            this.imagepasteEvictionCount++;
            this.onImagepasteBitmapEvicted({
                seq: c.seq,
                evictedBytes: c.bytes,
                capBytes: this.imagepasteBitmapBytesCap,
            });
            c.evict();
        }
    }
    /**
     * (Adversarial-review fix C1) Mark any `'pending'` imagepaste whose fetch
     * has been outstanding for `pendingImagepasteTimeoutMs` or longer as
     * `'failed'` — the safety valve that guarantees `bakeStep`'s stall (below)
     * can never be permanent, even if `CanvasDrawingEngine`'s fetch neither
     * resolves nor calls `onImagepasteBitmap(seq, null)` (it always does one
     * or the other on every code path today, per the fix's report — this is
     * pure defense in depth against a future regression / a fetch that hangs
     * forever with no network-level timeout). Called at the top of every
     * `bakeStep()`.
     */
    expireStalePendingImagepastes() {
        const now = this.now();
        for (const [seq, rec] of this.imagepastes) {
            if (rec.status !== 'pending')
                continue;
            if (now - rec.pendingSince < this.pendingImagepasteTimeoutMs)
                continue;
            this.imagepastes.set(seq, { ...rec, status: 'failed', bitmap: null });
        }
        for (const rec of this.regions.values()) {
            if (rec.status !== 'pending')
                continue;
            if (now - rec.pendingSince < this.pendingImagepasteTimeoutMs)
                continue;
            rec.status = 'failed';
        }
    }
    /** Remove a baked/trimmed region record, releasing its retained bitmaps' byte accounting. */
    trimRegion(seq) {
        const rec = this.regions.get(seq);
        if (rec !== undefined) {
            for (const b of rec.bitmaps)
                if (b !== null)
                    this.pendingImagepasteBytes -= b.rgba.byteLength;
        }
        this.regions.delete(seq);
    }
    /** Remove a baked/trimmed imagepaste record, releasing any retained bitmap's byte accounting (C2). */
    trimImagepaste(seq) {
        const rec = this.imagepastes.get(seq);
        if (rec !== undefined && rec.bitmap !== null) {
            this.pendingImagepasteBytes -= rec.bitmap.rgba.byteLength;
        }
        this.imagepastes.delete(seq);
    }
    /**
     * `whiteboard.region` (範囲の移動・コピー) のメタを受信時点で同期的に取り込む
     * (`onImagepaste` と同じ理由: 画像の fetch+decode より後の `snapshot.published` /
     * `stroke.cancelled` の窓が、この seq に届く前に記録されている必要がある)。
     * seq 単位で冪等。画像は `onRegionImages` で後から付ける。
     */
    onRegion(meta) {
        if (this.regions.has(meta.seq))
            return;
        this.regions.set(meta.seq, {
            meta,
            footprints: regionMoveFootprints(meta, this.width, this.height),
            bitmaps: meta.items.map(() => null),
            status: 'pending',
            pendingSince: this.now(),
            cancelled: false,
        });
    }
    /**
     * `whiteboard.region-cancelled` (範囲の移動・コピーの取り消し) の tombstone。線の `onCancelled` と
     * 同じく、その命令を除いて元と置き先の矩形 (全対象層) を作り直す対象にする (実際の作り直しは
     * `drainRewind` で同 tick にまとめる)。以後 bake にも再生にも寄与しない。
     * 未知 seq (既に焼かれた / 一度も受けていない) と取り消し済みは冪等に無視。画像が未着 / 失敗でも
     * 矛盾しない (画像は要らなくなるので stall の対象からも外れる)。
     * 戻りは作り直し対象になった矩形 (無ければ null)。
     */
    onRegionCancelled(regionSeq) {
        const rec = this.regions.get(regionSeq);
        if (rec === undefined || rec.cancelled)
            return null;
        rec.cancelled = true;
        // 画像はもう使わない (メモリ予算を返す)。
        for (const b of rec.bitmaps)
            if (b !== null)
                this.pendingImagepasteBytes -= b.rgba.byteLength;
        rec.bitmaps = rec.meta.items.map(() => null);
        for (const f of rec.footprints) {
            const list = this.pendingCancelBboxByLayer.get(f.layerId);
            if (list === undefined)
                this.pendingCancelBboxByLayer.set(f.layerId, [f.rect]);
            else
                list.push(f.rect);
        }
        return rec.footprints;
    }
    /**
     * `onRegion` した命令 (`seq`) の画像 (`items` と同じ順) を付ける。1 つでも null / 欠け /
     * サイズ不一致 (width×height が src の w×h と違う) なら全体を 'failed' にする
     * (部分成功は「消えて貼られない」の元。ライブ側も全体を適用しない)。
     * `images` が null も 'failed'。未知の seq / 既に 'resolved' は no-op。
     * 画像は dst でキャンバスにクリップして保持する (貼り付けと同じ C2 の考え方)。
     */
    onRegionImages(seq, images) {
        const rec = this.regions.get(seq);
        if (rec === undefined || rec.status === 'resolved' || rec.cancelled)
            return;
        const { meta } = rec;
        const ok = images !== null &&
            images.length === meta.items.length &&
            images.every((img) => img !== null &&
                img !== undefined &&
                img.width === meta.src.w &&
                img.height === meta.src.h &&
                img.rgba.length === img.width * img.height * 4);
        if (!ok || images === null) {
            rec.status = 'failed';
            rec.bitmaps = meta.items.map(() => null);
            return;
        }
        const bitmaps = images.map((img) => img === null || img === undefined
            ? null
            : this.clipImagepasteBitmap(meta.dst.x, meta.dst.y, img));
        rec.bitmaps = bitmaps;
        rec.status = 'resolved';
        for (const b of bitmaps)
            if (b !== null)
                this.pendingImagepasteBytes += b.rgba.byteLength;
        // 追跡の合計が上限を超えていたら古い順に落とす (自分自身も対象になりうる)。
        this.enforceImagepasteBitmapCap();
    }
    /** この命令 (`seq`) の矩形を `repairRegion` で作り直せるか (decode 済みで、まだ baseline に焼かれていない)。 */
    canRepairRegion(seq) {
        const rec = this.regions.get(seq);
        return (rec !== undefined &&
            rec.status === 'resolved' &&
            !rec.cancelled &&
            rec.meta.seq > this.watermark);
    }
    /**
     * この命令が絵を変える (層, 矩形) の両方 (move は src 側 + dst 側)。未追跡 / 既に焼かれた
     * seq は `undefined`。conflict-aware Undo のクライアント側の衝突判定・作り直しの対象矩形に使う。
     */
    getRegionFootprints(seq) {
        return this.regions.get(seq)?.footprints;
    }
    /**
     * Record a `whiteboard.clear` broadcast (2026-07-23「第二弾設計確定」
     * imagepaste/per-layer-clear × Undo 穴埋め — without this a clear landing
     * between a cancelled stroke's `endSeq` and the current watermark is
     * silently missing from both the baseline bake and any bbox-local rewind
     * that spans it). `layerId` mirrors the broadcast's own field: `undefined`
     * = full clear (every layer), a concrete id = that layer only. Idempotent
     * per `seq`.
     */
    onClear(layerId, seq) {
        if (this.clears.has(seq))
            return;
        this.clears.set(seq, { seq, layerId });
        // X-1b (2026-08-12、feedback-triage-2026-08-12.md「B-2 の副産物」X-1): clear は
        // **該当レイヤーの in-flight stroke を殺す**。`StrokeRenderer.clearLayer` が
        // まさにそうしており (`strokeRenderer.ts:439-446`、テスト `drops in-flight
        // strokes for the cleared layer`)、ライブ表示ではその stroke は死んでいる。
        //
        // 【なぜ store 側にも要るか】`bakeStep` / `localRepair` は **seq 順 merge** で、
        // stroke の順序キーは `endSeq` (module header「Replay-order note」)。よって
        // `startSeq < clearSeq < endSeq` の跨ぎ stroke は **clear の後に baseline へ
        // 焼かれる** — ライブでは clear が殺した線が、baseline では蘇る。以後その領域を
        // rewind すると patch が**死んだ線を画面に復活させる**。
        //
        // サーバ側 (`SessionBuffer` の同名 X-1 修正) だけを直すと、この baseline との
        // 乖離が残る。両方を同じ意味論に揃える必要がある。
        //
        // 実装: 該当レイヤーの **未 end (endSeq === null)** な追跡 stroke を落とす。
        // 「殺す」= 記録ごと削除でよい (cancelled フラグではない) — clear 済みの線は
        // baseline にもどの replay にも寄与すべきでないため。既に end 済みの stroke は
        // 触らない (seq 順 merge で clear の前後関係が正しく効く)。
        for (const [sid, rec] of this.strokes) {
            if (rec.endSeq !== null)
                continue;
            if (layerId !== undefined && rec.layerId !== layerId)
                continue;
            this.strokes.delete(sid);
        }
    }
    /** `snapshot.published { snapshotSeq }` (f4) — advances the bake TARGET only; bake itself is lazy. */
    onSnapshotPublished(snapshotSeq) {
        if (snapshotSeq > this.targetSnapshotSeq)
            this.targetSnapshotSeq = snapshotSeq;
    }
    // ─── rewind (cancel → bbox-local repair) ────────────────────────────────
    /** Whether any `stroke.cancelled` is queued and not yet drained. */
    hasPendingRewind() {
        return this.pendingCancelBboxByLayer.size > 0;
    }
    /**
     * Compute + return one `RewindPatch` per affected layer, coalescing every
     * bbox queued since the last drain (f4: "同 tick に溜まった cancel を union
     * bbox で 1 パス処理"). Clears the pending queue. Callers should call this
     * once per batch of incoming events (e.g. once at the end of processing a
     * `deltaEvents`/`events` array, or once per microtask after one or more
     * `stroke.cancelled` broadcasts land) rather than after every single
     * `onCancelled` call, so N cancels queued in the same tick produce ONE
     * replay pass per layer instead of N.
     */
    drainRewind() {
        const patches = [];
        for (const [layerId, rects] of this.pendingCancelBboxByLayer) {
            const union = unionRects(rects);
            if (union === null)
                continue;
            const patch = this.localRepair(layerId, union);
            if (patch !== null)
                patches.push(patch);
        }
        this.pendingCancelBboxByLayer.clear();
        return patches;
    }
    /**
     * 画像貼り付けの矩形を「baseline + watermark 以降の全イベント (seq 順)」から今すぐ
     * 作り直す (docs/_wip/imagepaste-seq-repair-2026-10-15.md 案 (a))。画像の decode が
     * 遅れて、その間に後続の線が先に手元へ描かれたときに、server の snapshot と同じ
     * seq 順の絵へ矩形だけ直すために使う。描きかけの線は点列の途中までを最後に足す。
     * `bbox` はキャンバスにクランプされる。戻りは `layer.region.apply` へそのまま渡せる。
     */
    repairRegion(layerId, bbox) {
        const clamped = {
            x0: Math.max(0, Math.floor(bbox.x0)),
            y0: Math.max(0, Math.floor(bbox.y0)),
            x1: Math.min(this.width - 1, Math.floor(bbox.x1)),
            y1: Math.min(this.height - 1, Math.floor(bbox.y1)),
        };
        if (clamped.x1 < clamped.x0 || clamped.y1 < clamped.y0)
            return null;
        return this.localRepair(layerId, clamped, true);
    }
    /**
     * この貼り付け (`seq`) の矩形を `repairRegion` で作り直せるか。decode 済みで、
     * まだ baseline に焼かれていない (watermark より後) 場合のみ true。それ以外
     * (未追跡・失敗・画面外・タイムアウトで baseline から飛ばされた) は呼び出し側が
     * 画像の直書きにフォールバックする。
     */
    canRepairImagepaste(seq) {
        const rec = this.imagepastes.get(seq);
        return (rec !== undefined &&
            rec.status === 'resolved' &&
            rec.bitmap !== null &&
            rec.seq > this.watermark);
    }
    /**
     * bbox-local repair (docs 「B' bbox 局所 replay」): seed the scratch surface
     * from the baseline's `layerId`, replay every non-cancelled, already-ended,
     * bbox-intersecting stroke past `watermark` (in `endSeq` order), then read
     * back just the `bbox` region.
     *
     * "巻き戻し replay は通常描画と同一パイプライン" (no dedicated fast path):
     * this calls the SAME `EngineCore.startStroke`/`addPoints`/`endStroke` the
     * live engine uses, just against a scratch surface.
     *
     * **Write clip (2026-07-23, 「stamp 書き込みの bbox clip」)**: `this.scratch`
     * is set to clip every stamp/erase write to `bbox` for the duration of the
     * replay loop below. This is what keeps a long intersecting stroke's
     * replay cheap — coordinates are NEVER transformed (rasterizer.ts
     * `StampParams.clip` contract), so this is purely additive and byte-exact
     * with the unclipped path for every pixel actually read back (pinned by
     * `replayEquivalence.property.test.ts`, which exercises this same
     * `localRepair`-shaped path through the real store). `this.scratch` is a
     * dedicated single-purpose surface (never used for anything else), so
     * there's no risk of the clip leaking into unrelated work — it's cleared
     * in a `finally` regardless, purely for hygiene / defensiveness.
     *
     * **imagepaste / clear merge (2026-07-23「第二弾設計確定」)**: in addition
     * to intersecting strokes, this also replays any past-`watermark`
     * `whiteboard.imagepaste` targeting `layerId` (bbox-intersecting, `bitmap
     * !== null` — i.e. `status === 'resolved'` AND on-canvas; a still-`
     * 'pending'` or `'failed'` paste is skipped here, matching what the LIVE
     * canvas shows at this exact moment: nothing painted yet, or a confirmed
     * failure — unlike `bakeStep`, a rewind's `watermark` doesn't move, so
     * skipping a not-yet-resolved paste here is never a PERMANENT divergence,
     * only a momentary one that a later rewind naturally repaints correctly
     * once `onImagepasteBitmap` resolves it) and any past-`watermark`
     * `whiteboard.clear` that affects
     * `layerId` (per-layer match, OR a full clear — `layerId === undefined`ⁿ
     * always applies, mirroring the broadcast's own "every layer" semantics).
     * A clear is NOT bbox-filtered (it wipes the whole layer, not a local
     * region) — `this.scratch.clearLayer(0)` unconditionally zeroes the entire
     * scratch buffer, which `setWriteClip` does not gate (`EngineCore.clearLayer`
     * / `StrokeRenderer.clearLayer` write the full buffer directly, matching
     * `applySnapshot`'s own clamp-free clear path). All three kinds are merged
     * into ONE seq-ordered replay sequence (they share a single server seq
     * space, module header) so a stroke → clear → stroke interleaving replays
     * in the exact order the server's own PNG-generating worker applies it.
     */
    localRepair(layerId, bbox, includeInFlight = false) {
        const baselineLayer = this.baseline.getLayer(layerId);
        const scratchLayer = this.scratch.getLayer(0);
        if (baselineLayer === undefined || scratchLayer === undefined)
            return null;
        // Fast persistent-buffer reseed (typed-array `.set()` = one memcpy) —
        // cheaper than a fresh `EngineCore` + `applySnapshot` per rewind.
        scratchLayer.main.set(baselineLayer.main);
        const toReplay = [];
        for (const rec of this.strokes.values()) {
            if (rec.layerId !== layerId)
                continue;
            if (rec.endSeq === null || rec.endSeq <= this.watermark)
                continue; // already in baseline
            if (rec.cancelled)
                continue; // tombstoned — never contributes pixels
            if (rec.bbox === null || !rectsIntersect(rec.bbox, bbox))
                continue; // locality (C 案保証)
            toReplay.push({ kind: 'stroke', seq: rec.endSeq, rec });
        }
        for (const rec of this.imagepastes.values()) {
            if (rec.layerId !== layerId)
                continue;
            if (rec.seq <= this.watermark)
                continue; // already in baseline
            if (rec.bitmap === null)
                continue; // not fetched yet / failed — skip (matches display)
            if (rec.bbox === null || !rectsIntersect(rec.bbox, bbox))
                continue; // locality
            toReplay.push({ kind: 'imagepaste', seq: rec.seq, rec });
        }
        for (const rec of this.regions.values()) {
            if (rec.meta.seq <= this.watermark)
                continue; // already in baseline
            if (rec.cancelled)
                continue; // tombstoned — never contributes pixels
            if (rec.status !== 'resolved')
                continue; // not fetched yet / failed — skip (matches display)
            // この層で、bbox に触れる (消す側 or 置く側の) 矩形があるときだけ再生する。
            const touches = rec.footprints.some((f) => f.layerId === layerId && rectsIntersect(f.rect, bbox));
            if (!touches)
                continue;
            toReplay.push({ kind: 'region', seq: rec.meta.seq, rec });
        }
        for (const rec of this.clears.values()) {
            if (rec.layerId !== undefined && rec.layerId !== layerId)
                continue; // targets a different layer
            if (rec.seq <= this.watermark)
                continue; // already in baseline
            // No bbox filter — a clear affects the whole layer, so it's always
            // relevant once it's past the watermark and targets this layer.
            toReplay.push({ kind: 'clear', seq: rec.seq, rec });
        }
        if (includeInFlight) {
            // 描きかけ (stroke.end 未着) の線: 手元のキャンバスには既に途中まで描かれている
            // ので、作り直しで消さないよう「ここまでの点列」を最後に足す。遅延 commit の
            // ペン (flat/marker/tone) は buffer が stroke.end で main の上に載るので足さない
            // (足すと半透明が二重になる)。
            for (const rec of this.strokes.values()) {
                if (rec.layerId !== layerId || rec.endSeq !== null || rec.cancelled)
                    continue;
                if (isFlatPen(rec.pen) || rec.points.length === 0)
                    continue;
                const live = strokeFootprintBbox(rec.points, rec.width, this.width, this.height);
                if (live === null || !rectsIntersect(live, bbox))
                    continue;
                toReplay.push({ kind: 'stroke', seq: IN_FLIGHT_SEQ, rec });
            }
        }
        toReplay.sort((a, b) => a.seq - b.seq);
        this.scratch.setWriteClip(bbox);
        try {
            for (const entry of toReplay)
                this.replayEntryOntoScratch(entry, layerId);
        }
        finally {
            this.scratch.setWriteClip(null);
        }
        const w = bbox.x1 - bbox.x0 + 1;
        const h = bbox.y1 - bbox.y0 + 1;
        const rgba = new Uint8ClampedArray(w * h * 4);
        const rowBytes = w * 4;
        for (let y = 0; y < h; y++) {
            const srcStart = ((bbox.y0 + y) * this.width + bbox.x0) * 4;
            rgba.set(scratchLayer.main.subarray(srcStart, srcStart + rowBytes), y * rowBytes);
        }
        return { layerId, bbox, rgba };
    }
    replayEntryOntoScratch(entry, layerId) {
        switch (entry.kind) {
            case 'region':
                this.replayRegion(this.scratch, entry.rec, layerId);
                return;
            case 'stroke':
                this.replayOntoScratch(entry.rec);
                return;
            case 'imagepaste':
                this.replayImagepasteOntoScratch(entry.rec);
                return;
            case 'clear':
                // Scratch is a single-layer (layerId 0) surface regardless of which
                // real layer we're repairing — mirrors `replayOntoScratch`'s own
                // `layerId: 0` remap.
                this.scratch.clearLayer(0);
                return;
        }
    }
    replayOntoScratch(rec) {
        this.scratch.startStroke({
            sid: rec.sid,
            pen: rec.pen,
            color: rec.color,
            alpha: rec.alpha,
            width: rec.width,
            layerId: 0,
            mask: rec.mask,
            aa: rec.aa,
            ...(rec.penParams !== undefined ? { penParams: rec.penParams } : {}),
            nowMs: 0,
        });
        if (rec.points.length > 0)
            this.scratch.addPoints(rec.sid, rec.points, 0);
        this.scratch.endStroke(rec.sid);
    }
    /**
     * 範囲の移動 1 件を `core` に再生する (「消す → 置く」の順、`applyRegionMove` と同じ)。
     * `onlyLayer === null` は baseline (実際の層 id)、数値は scratch (その層だけを層 0 に写して再生)。
     */
    replayRegion(core, rec, onlyLayer) {
        const { meta } = rec;
        const target = (id) => onlyLayer === null ? id : id === onlyLayer ? 0 : null;
        if (meta.mode === 'move') {
            for (const item of meta.items) {
                const t = target(item.srcLayerId);
                if (t !== null)
                    core.clearRegion(t, meta.src.x, meta.src.y, meta.src.w, meta.src.h);
            }
        }
        for (let i = 0; i < meta.items.length; i++) {
            const item = meta.items[i];
            const bitmap = rec.bitmaps[i];
            if (item === undefined || bitmap === undefined || bitmap === null)
                continue;
            const t = target(item.dstLayerId);
            if (t === null)
                continue;
            core.applyImagepaste({
                layerId: t,
                rgba: bitmap.rgba,
                width: bitmap.width,
                height: bitmap.height,
                x: bitmap.x,
                y: bitmap.y,
            });
        }
    }
    replayImagepasteOntoScratch(rec) {
        const bitmap = rec.bitmap;
        if (bitmap === null)
            return; // defensive — callers already filter this out
        this.scratch.applyImagepaste({
            layerId: 0, // scratch is always single-layer (see `replayOntoScratch`)
            rgba: bitmap.rgba,
            width: bitmap.width,
            height: bitmap.height,
            // Use the CLIPPED bitmap's own (x, y), not `rec.x`/`rec.y` (C2) — see
            // `ClippedImagepasteBitmap`'s doc for why these are byte-exact
            // equivalent to painting the original unclipped buffer at `rec.x/y`.
            x: bitmap.x,
            y: bitmap.y,
        });
    }
    // ─── bake (snapshot.published → advance the baseline watermark) ────────
    /** Whether `targetSnapshotSeq > watermark` (more bake work outstanding). */
    hasPendingBake() {
        return this.targetSnapshotSeq > this.watermark;
    }
    /** Current baseline watermark — the basis `localRepair` rewinds against (f4). */
    getWatermark() {
        return this.watermark;
    }
    /**
     * Time-sliced bake step (f4 "bake は lazy / time-slice 可 … per-stroke
     * atomic"). Processes up to `bakeStepLimit` eligible strokes (`endSeq !==
     * null && endSeq <= targetSnapshotSeq`), ascending by `endSeq`:
     *
     *   - non-cancelled → replayed onto `baseline` (bakes its pixels in)
     *   - cancelled      → skipped (tombstone — never baked)
     *
     * Either way the stroke is then trimmed from `strokes` (f4 "watermark を
     * 進めて stroke リストを trim") and `watermark` advances to its `endSeq`.
     * A `stroke.cancelled` interleaved between two `bakeStep()` calls is safe:
     * `drainRewind()` always reads the CURRENT `watermark` + `strokes`, and
     * per-stroke atomicity means there's never a torn intermediate state
     * (this method never `await`s).
     *
     * In-flight (not yet ended) strokes are naturally excluded (`endSeq ===
     * null`) — this is exactly the f4 bake rule ("endSeq ≤ snapshotSeq かつ非
     * cancel 済み"), no bridging-stroke special-case needed.
     *
     * Returns `true` if more eligible work remains (caller should re-invoke,
     * e.g. on the next idle tick / microtask), `false` once fully caught up
     * to `targetSnapshotSeq` (mod any not-yet-ended bridging strokes, which
     * become eligible once their `stroke.end` lands).
     *
     * **imagepaste / clear merge (2026-07-23「第二弾設計確定」)**: eligible
     * `whiteboard.imagepaste` (`seq <= targetSnapshotSeq`) and `whiteboard.clear`
     * (`seq <= targetSnapshotSeq`) entries are merged into the SAME seq-ordered
     * eligible-batch as strokes (they share one server seq space, module
     * header), so a stroke → clear → stroke sequence bakes in the exact order
     * the server's own PNG worker applies it — a clear reaching its turn wipes
     * the baseline layer it targets before any LATER stroke in the same batch
     * bakes on top of it. A `'failed'` imagepaste (confirmed fetch failure,
     * pending-timeout, or C2 cap eviction) is skipped like it never happened
     * (bitmap stays `null`) but STILL trimmed/advances the watermark past it —
     * that divergence is accepted (matches what the live canvas also shows:
     * nothing painted).
     *
     * **Adversarial-review fix (C1, 2026-07-24) — the pending-imagepaste
     * STALL**: unlike a `'failed'` paste, a still-`'pending'` one (fetch+decode
     * genuinely still in flight — `onImagepasteBitmap` hasn't settled it
     * either way yet) must NEVER be skipped-and-trimmed, because that would
     * silently and PERMANENTLY diverge the baseline from the live canvas once
     * the fetch eventually succeeds (the pre-fix bug this method now closes —
     * see `ImagepasteSettleState`'s doc). Instead, this method computes
     * `stallAtSeq` = the lowest `seq` among still-`'pending'` eligible
     * imagepastes, and excludes every entry (of ANY kind — strokes and clears
     * too, since `watermark` is one shared cut point across all three) at or
     * past that seq from THIS batch. The watermark can therefore never advance
     * past an unsettled paste. Once `onImagepasteBitmap` (or the
     * `expireStalePendingImagepastes` safety valve) settles it one way or the
     * other, the NEXT `bakeStep()` call picks up exactly where it left off —
     * no special-casing needed, `stallAtSeq` is recomputed fresh every call.
     * This mirrors the pre-existing not-yet-ended-stroke exclusion above (same
     * "wait, don't skip" shape), just triggered by settle state instead of
     * `endSeq === null`.
     */
    bakeStep() {
        if (!this.hasPendingBake())
            return false;
        this.expireStalePendingImagepastes();
        let stallAtSeq = Number.POSITIVE_INFINITY;
        for (const rec of this.imagepastes.values()) {
            if (rec.status !== 'pending')
                continue;
            if (rec.seq > this.targetSnapshotSeq)
                continue; // not eligible yet regardless — can't block anything
            if (rec.seq < stallAtSeq)
                stallAtSeq = rec.seq;
        }
        for (const rec of this.regions.values()) {
            if (rec.status !== 'pending' || rec.cancelled)
                continue;
            if (rec.meta.seq > this.targetSnapshotSeq)
                continue;
            if (rec.meta.seq < stallAtSeq)
                stallAtSeq = rec.meta.seq;
        }
        const eligible = [];
        for (const rec of this.strokes.values()) {
            if (rec.endSeq === null || rec.endSeq > this.targetSnapshotSeq)
                continue;
            if (rec.endSeq >= stallAtSeq)
                continue; // held behind the unsettled paste
            eligible.push({ kind: 'stroke', seq: rec.endSeq, rec });
        }
        for (const rec of this.imagepastes.values()) {
            if (rec.seq > this.targetSnapshotSeq)
                continue;
            if (rec.seq >= stallAtSeq)
                continue;
            eligible.push({ kind: 'imagepaste', seq: rec.seq, rec });
        }
        for (const rec of this.regions.values()) {
            if (rec.meta.seq > this.targetSnapshotSeq)
                continue;
            if (rec.meta.seq >= stallAtSeq)
                continue;
            eligible.push({ kind: 'region', seq: rec.meta.seq, rec });
        }
        for (const rec of this.clears.values()) {
            if (rec.seq > this.targetSnapshotSeq)
                continue;
            if (rec.seq >= stallAtSeq)
                continue;
            eligible.push({ kind: 'clear', seq: rec.seq, rec });
        }
        eligible.sort((a, b) => a.seq - b.seq);
        const batch = eligible.slice(0, this.bakeStepLimit);
        for (const entry of batch) {
            switch (entry.kind) {
                case 'stroke':
                    if (!entry.rec.cancelled)
                        this.bakeOntoBaseline(entry.rec);
                    this.strokes.delete(entry.rec.sid);
                    break;
                case 'imagepaste':
                    // Every entry reaching here is already settled ('resolved' or
                    // 'failed') — a 'pending' one can never be < stallAtSeq (it WOULD
                    // define stallAtSeq, or an earlier one already does).
                    if (entry.rec.bitmap !== null)
                        this.bakeImagepasteOntoBaseline(entry.rec);
                    this.trimImagepaste(entry.rec.seq);
                    break;
                case 'clear':
                    this.baseline.clearLayer(entry.rec.layerId);
                    this.clears.delete(entry.rec.seq);
                    break;
                case 'region':
                    // 'failed' は全体を飛ばす (ライブ側も何も適用しなかった)。
                    if (entry.rec.status === 'resolved' && !entry.rec.cancelled) {
                        this.replayRegion(this.baseline, entry.rec, null);
                    }
                    this.trimRegion(entry.rec.meta.seq);
                    break;
            }
            if (entry.seq > this.watermark)
                this.watermark = entry.seq;
        }
        // "More work remains" means more ALREADY-KNOWN eligible entries are
        // waiting behind the step cap — NOT that `targetSnapshotSeq` is still
        // ahead of `watermark` (that gap may simply be waiting on entries that
        // haven't arrived / ended yet, or on an unsettled paste's `stallAtSeq`,
        // neither of which is "pending work" the caller should busy-loop on —
        // the next externally-triggered `bakeStep()` call, or the pending-
        // timeout safety valve, is what eventually unblocks it).
        return eligible.length > batch.length;
    }
    bakeOntoBaseline(rec) {
        this.baseline.startStroke({
            sid: rec.sid,
            pen: rec.pen,
            color: rec.color,
            alpha: rec.alpha,
            width: rec.width,
            layerId: rec.layerId,
            mask: rec.mask,
            aa: rec.aa,
            ...(rec.penParams !== undefined ? { penParams: rec.penParams } : {}),
            nowMs: 0,
        });
        if (rec.points.length > 0)
            this.baseline.addPoints(rec.sid, rec.points, 0);
        this.baseline.endStroke(rec.sid);
    }
    bakeImagepasteOntoBaseline(rec) {
        const bitmap = rec.bitmap;
        if (bitmap === null)
            return; // defensive — callers already filter this out
        this.baseline.applyImagepaste({
            layerId: rec.layerId,
            rgba: bitmap.rgba,
            width: bitmap.width,
            height: bitmap.height,
            // CLIPPED bitmap's own (x, y) (C2) — see `ClippedImagepasteBitmap`'s doc.
            x: bitmap.x,
            y: bitmap.y,
        });
    }
    // ─── introspection (tests / diagnostics) ────────────────────────────────
    /**
     * Read-only accessor: the (immutable, post-`stroke.end`) bbox of a
     * currently-tracked stroke, keyed by `sid`. `undefined` for an unknown
     * sid, a not-yet-ended stroke (`onStrokeEnd` not observed — bbox is only
     * computed there), or an ended stroke whose bbox came out `null` (empty /
     * fully-off-canvas points, same case `onCancelled` treats as "nothing to
     * rewind"). Cancelled-but-not-yet-baked strokes still return their bbox
     * (the tombstone doesn't erase the record until `bakeStep()` trims it).
     * Never mutates `strokes` / `watermark` / the pending rewind queue.
     *
     * Added for the conflict-aware Undo REJECTION UI (bbox ハイライト,
     * docs/_wip/undo-conflict-aware-2026-07-12.md「B' 実装での追加事項」): a
     * `stroke.cancel-rejected { reason: 'conflict' }` payload only carries
     * `{ sid, uid }` per conflicting stroke (protocol-events.md §1.1,
     * intentionally lightweight — no bbox on the wire), so the presentation
     * layer resolves those sids back to a bbox via this accessor to draw an
     * on-canvas highlight (`CanvasDrawingEngine.getUndoConflictBbox` is the
     * thin public wrapper other layers call). A conflicting stroke that is
     * still in-flight (growing bbox, f2) is NOT resolvable here — its bbox is
     * computed only on `stroke.end` — so callers must treat `undefined` as
     * "no highlight for this sid", not an error.
     */
    getStrokeBbox(sid) {
        return this.strokes.get(sid)?.bbox ?? undefined;
    }
    /** Number of strokes currently tracked (not yet baked or trimmed). For tests. */
    getTrackedStrokeCount() {
        return this.strokes.size;
    }
    /** Whether `sid` is currently tracked (in flight, ended-but-unbaked, or cancelled-but-unbaked). */
    hasStroke(sid) {
        return this.strokes.has(sid);
    }
    /** Whether an `onImagepaste`-ingested paste (`seq`) is still tracked (not yet baked/trimmed). For tests. */
    hasImagepaste(seq) {
        return this.imagepastes.has(seq);
    }
    /** Number of imagepaste records currently tracked (not yet baked or trimmed). For tests. */
    getTrackedImagepasteCount() {
        return this.imagepastes.size;
    }
    /**
     * Settle state (`'pending' | 'resolved' | 'failed'`) of a tracked
     * imagepaste, or `undefined` if `seq` is unknown (never ingested, or
     * already baked/trimmed). C1 introspection, for tests.
     */
    getImagepasteStatus(seq) {
        return this.imagepastes.get(seq)?.status;
    }
    /**
     * Running total of `ClippedImagepasteBitmap.rgba.byteLength` across every
     * currently-tracked, resolved imagepaste (C2 cap accounting). For tests /
     * diagnostics.
     */
    getPendingImagepasteBytes() {
        return this.pendingImagepasteBytes;
    }
    /** Count of C2 cap evictions so far (`imagepasteBitmapBytesCap` exceeded). For tests / diagnostics. */
    getImagepasteEvictionCount() {
        return this.imagepasteEvictionCount;
    }
    /** Whether an `onRegion`-ingested region (`seq`) is still tracked (not yet baked/trimmed). For tests. */
    hasRegion(seq) {
        return this.regions.has(seq);
    }
    /** その region が取り消し済み (tombstone 受信済み) か。未追跡は false。 */
    isRegionCancelled(seq) {
        return this.regions.get(seq)?.cancelled === true;
    }
    /** Settle state of a tracked region (`undefined` if unknown / already baked). For tests. */
    getRegionStatus(seq) {
        return this.regions.get(seq)?.status;
    }
    /** Whether an `onClear`-ingested clear (`seq`) is still tracked (not yet baked/trimmed). For tests. */
    hasClear(seq) {
        return this.clears.has(seq);
    }
    /** Direct baseline layer access — for tests that want to assert baked pixels. */
    getBaselineLayerMain(layerId) {
        return this.baseline.getLayer(layerId)?.main;
    }
}
//# sourceMappingURL=UndoBaselineStore.js.map